import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreHorizontal, Plus, Tag as TagIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { createTag, deleteTag, listTags, patchTag } from "@/api/tags";
import { ApiError } from "@/api/client";
import type { TagOut } from "@/api/schemas";
import { TagChip } from "@/components/ticket/tag-chip";
import { TAG_PALETTE } from "@/components/ticket/tag-palette";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableBodySkeleton } from "@/components/ui/skeleton";
import { TBody, TD, TH, THead, TR, Table } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";

const TAGS_QUERY = ["tags"] as const;

function TagsAdminPage() {
  const { data: tags, isLoading } = useQuery({
    queryKey: TAGS_QUERY,
    queryFn: listTags,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TagOut | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TagOut | null>(null);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium tracking-tight">Tags</h1>
          <p className="mt-1 text-sm text-text-muted">
            Tags organize tickets — categorize by topic, priority, or customer
            segment.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Create tag
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-surface">
        {isLoading ? (
          <Table>
            <THead>
              <TR>
                <TH>Tag</TH>
                <TH>Color</TH>
                <TH>Created</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TableBodySkeleton rows={4} cells={4} />
          </Table>
        ) : !tags || tags.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
              <TagIcon className="h-4 w-4 text-text-muted" />
            </div>
            <div className="text-sm font-medium">No tags yet</div>
            <div className="mt-1 text-xs text-text-muted">
              Click <span className="font-mono">Create tag</span> to add one.
            </div>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Tag</TH>
                <TH>Color</TH>
                <TH>Created</TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {tags.map((t) => (
                <TR key={t.id}>
                  <TD className="font-medium">
                    <TagChip name={t.name} color={t.color} />
                  </TD>
                  <TD className="font-mono text-xs text-text-muted">{t.color}</TD>
                  <TD className="text-xs text-text-muted">
                    {formatAge(t.created_at)}
                  </TD>
                  <TD>
                    <RowActionMenu
                      onEdit={() => setEditTarget(t)}
                      onDelete={() => setDeleteTarget(t)}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDialog tag={editTarget} onOpenChange={(o) => !o && setEditTarget(null)} />
      <DeleteDialog
        tag={deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />
    </div>
  );
}

function RowActionMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const role = useAuth((s) => s.user?.role);
  const canDelete = role === "admin" || role === "superadmin";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive disabled={!canDelete} onSelect={onDelete}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type FormValues = { name: string; color: string };

function CreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    defaultValues: { name: "", color: TAG_PALETTE[0] },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => createTag(v),
    onSuccess: (created) => {
      toast.success(`Created ${created.name}`);
      void qc.invalidateQueries({ queryKey: TAGS_QUERY });
      onOpenChange(false);
      form.reset({ name: "", color: TAG_PALETTE[0] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "tag_name_taken") {
        toast.error("A tag with this name already exists");
      } else {
        toast.error(e instanceof ApiError ? e.message : "Could not create tag");
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset({ name: "", color: TAG_PALETTE[0] });
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create tag</DialogTitle>
          <DialogDescription>
            Pick a short name and a color — tags are shown as chips on tickets.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", { required: true, minLength: 1, maxLength: 50 })}
              placeholder="billing"
              autoFocus
            />
          </FormField>
          <FormField label="Color">
            <ColorSwatches
              value={form.watch("color")}
              onChange={(c) => form.setValue("color", c)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  tag,
  onOpenChange,
}: {
  tag: TagOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    values: tag ? { name: tag.name, color: tag.color } : undefined,
  });

  const mutation = useMutation({
    mutationFn: (body: Partial<FormValues>) => {
      if (!tag) throw new Error("no tag");
      return patchTag(tag.id, body);
    },
    onSuccess: (updated) => {
      toast.success(`Updated ${updated.name}`);
      void qc.invalidateQueries({ queryKey: TAGS_QUERY });
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "tag_name_taken") {
        toast.error("A tag with this name already exists");
      } else {
        toast.error(e instanceof ApiError ? e.message : "Could not update tag");
      }
    },
  });

  if (!tag) return null;

  return (
    <Dialog open={!!tag} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit tag</DialogTitle>
          <DialogDescription>
            <TagChip name={tag.name} color={tag.color} />
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => {
            const body: Partial<FormValues> = {};
            if (v.name !== tag.name) body.name = v.name.trim();
            if (v.color !== tag.color) body.color = v.color;
            if (Object.keys(body).length === 0) {
              onOpenChange(false);
              return;
            }
            mutation.mutate(body);
          })}
          className="flex flex-col gap-3"
        >
          <FormField label="Name">
            <Input
              {...form.register("name", { required: true, minLength: 1, maxLength: 50 })}
              autoFocus
            />
          </FormField>
          <FormField label="Color">
            <ColorSwatches
              value={form.watch("color")}
              onChange={(c) => form.setValue("color", c)}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  tag,
  onOpenChange,
}: {
  tag: TagOut | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => {
      if (!tag) throw new Error("no tag");
      return deleteTag(tag.id);
    },
    onSuccess: () => {
      toast.success(`Deleted ${tag?.name ?? "tag"}`);
      void qc.invalidateQueries({ queryKey: TAGS_QUERY });
      // Also refresh ticket detail queries so removed tag disappears from chips.
      void qc.invalidateQueries({ queryKey: ["ticket"] });
      void qc.invalidateQueries({ queryKey: ["tickets"] });
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Could not delete tag");
    },
  });

  if (!tag) return null;

  return (
    <Dialog open={!!tag} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete tag</DialogTitle>
          <DialogDescription>
            Delete <TagChip name={tag.name} color={tag.color} />? It will no
            longer appear on tickets. Historical mentions remain until you edit
            the ticket.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColorSwatches({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {TAG_PALETTE.map((color) => {
        const selected = color.toLowerCase() === value.toLowerCase();
        return (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            className={cn(
              "h-7 w-7 rounded-full border-2 transition",
              selected
                ? "border-text ring-2 ring-offset-2 ring-offset-bg"
                : "border-transparent hover:scale-110",
            )}
            style={{ backgroundColor: color }}
            aria-label={`Izaberi boju ${color}`}
            aria-pressed={selected}
          />
        );
      })}
    </div>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

function formatAge(iso: string): string {
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
}

export const Route = createFileRoute("/_auth/settings/tags")({
  component: TagsAdminPage,
});
